import { AUTH_TOKEN_STORAGE_KEY } from "./constants.js";

/**
 * Creates a fetch-compatible function that adds the composer token and sends
 * unauthorized users to the login page owned by the consuming application.
 *
 * @param {string} unauthorizedRedirectUrl
 * @param {{ tokenStorageKey?: string, fetchImplementation?: typeof fetch }} [config]
 * @returns {(input: RequestInfo | URL, init?: RequestInit) => Promise<Response>}
 */
export function withAuthFetch(unauthorizedRedirectUrl, config = {}) {
  if (
    typeof unauthorizedRedirectUrl !== "string" ||
    unauthorizedRedirectUrl.trim() === ""
  ) {
    throw new TypeError("withAuthFetch requires an unauthorized redirect URL");
  }

  const tokenStorageKey =
    config.tokenStorageKey || AUTH_TOKEN_STORAGE_KEY;

  return async function authenticatedFetch(input, init = {}) {
    const fetchImplementation =
      config.fetchImplementation ||
      (typeof globalThis !== "undefined" ? globalThis.fetch : undefined);

    if (typeof fetchImplementation !== "function") {
      throw new Error("Fetch is not available in this environment");
    }

    const headers = new Headers(init.headers || {});
    const token =
      typeof localStorage !== "undefined"
        ? localStorage.getItem(tokenStorageKey)
        : null;

    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }

    const response = await fetchImplementation(input, {
      ...init,
      headers,
    });

    if (
      (response.status === 401 || response.status === 403) &&
      typeof window !== "undefined"
    ) {
      window.location.href = unauthorizedRedirectUrl;
    }

    return response;
  };
}
